// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { materialsForYear, filterRows, total, recoveryRate, sumBy, totalsByYear } from '../data';
import { catInfo, FATES } from '../domain';
import { donutSvg, rankedBarsSvg, lineChartSvg, legendHtml, type Seg } from '../components/charts';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatTonnes, formatPercent, formatMt, fyShort } from '../utils/format';

export function renderOverview(root: HTMLElement, ctx: ViewContext): void {
  const d = ctx.data;
  const y = d.latestYear;
  const n = d.meta.national;
  const mats = materialsForYear(d, y);
  const plastics = mats.find((m) => m.key === 'plastics');
  const metals = mats.find((m) => m.key === 'metals');
  const byFate = sumBy(filterRows(d.rows, { yr: y }), (r) => r.f);
  const totalT = total(filterRows(d.rows, { yr: y }));
  const rate = recoveryRate(filterRows(d.rows, { yr: y }));
  const series = totalsByYear(d);

  const fateSegs: Seg[] = FATES.map((f) => ({ value: byFate.get(f.key) || 0, colour: f.colour, label: f.label, tip: `${f.label}: ${formatTonnes(byFate.get(f.key) || 0)} (${(((byFate.get(f.key) || 0) / totalT) * 100).toFixed(1)}%)` }));

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Where Australia's waste goes</h1>
      <p class="view-sub">Australia threw out <strong>${formatTonnes(totalT)}</strong> of waste in ${escapeHtml(fyShort(y))} — about
        ${n.perCapitaTonnes} tonnes per person. ${formatPercent(rate, 0)} of it was kept out of landfill. But
        whether a material is recovered depends almost entirely on what it's made of.</p>
    </div>

    <div class="hero">
      <div class="hero-panel">
        <div class="hero-lead">Recovery rate, latest year</div>
        <div class="big-ratio"><span>${formatPercent(metals?.rate ?? 0, 0)}</span><span class="vs">metals · vs ·</span><span class="lo">${formatPercent(plastics?.rate ?? 0, 0)}</span></div>
        <div class="hero-lead"><strong>Metals</strong> are recovered at ${formatPercent(metals?.rate ?? 0, 0)}; <strong>plastics</strong> at just
          ${formatPercent(plastics?.rate ?? 0, 0)}. Same bin, wildly different fate — the single biggest lever on the national
          ${glossaryTerm('recovery-rate', 'recovery rate')}.</div>
        <button class="btn-link" data-goto="materials">Compare every material →</button>
      </div>
      <div class="hero-panel hero-side">
        <div class="panel-title" style="text-align:center">Where every tonne ends up</div>
        <div class="panel-sub" style="text-align:center">${escapeHtml(fyShort(y))}, all materials</div>
        ${donutSvg(fateSegs, { centre: formatPercent(rate, 0), centreSub: 'recovered' })}
        ${legendHtml(FATES.map((f) => ({ colour: f.colour, label: f.label })))}
      </div>
    </div>

    <div class="stat-grid">
      <div class="stat"><div class="stat-label">Total waste ${escapeHtml(fyShort(y))}</div><div class="stat-value">${formatMt(totalT)} Mt</div><div class="stat-note">from ${n.totalFirstMt} Mt in ${escapeHtml(fyShort(d.firstYear))}</div></div>
      <div class="stat"><div class="stat-label">Resource recovery</div><div class="stat-value green">${formatPercent(rate, 0)}</div><div class="stat-note">up from ${formatPercent(n.recoveryRateFirst, 0)} in ${escapeHtml(fyShort(d.firstYear))}</div></div>
      <div class="stat"><div class="stat-label">Per person</div><div class="stat-value">${n.perCapitaTonnes} t</div><div class="stat-note">every Australian, each year</div></div>
      <div class="stat"><div class="stat-label">Gap to 2030 target</div><div class="stat-value amber">${formatPercent(n.targetGap, 0)}</div><div class="stat-note">target ${n.target}% recovery by 2030</div></div>
    </div>

    <div class="grid-2">
      <div class="panel" data-role="mats">
        <div class="panel-title">Biggest waste materials</div>
        <div class="panel-sub">Total tonnes in ${escapeHtml(fyShort(y))}, coloured by material. The badge shows how much of each is recovered. Click a bar for detail.</div>
        <div data-role="mat-bars"></div>
      </div>
      <div class="panel">
        <div class="panel-title">Recovery is slowly improving</div>
        <div class="panel-sub">National resource recovery rate over ${d.years.length} reporting years, against the 80%-by-2030 target.</div>
        ${lineChartSvg(
          [
            { label: 'Recovery rate', colour: 'var(--accent-primary)', points: series.map((s, i) => ({ x: i, y: s.rate })), width: 2.6 },
            { label: '2030 target', colour: 'var(--status-warn)', points: series.map((_, i) => ({ x: i, y: d.target.recoveryRate })), dashed: true, width: 1.5 },
          ],
          { width: 520, height: 240, xLabels: d.years.map(fyShort), yMax: 100, yFmt: (v) => `${v.toFixed(0)}%`, markers: true },
        )}
        ${legendHtml([{ colour: 'var(--accent-primary)', label: 'Recovery rate' }, { colour: 'var(--status-warn)', label: '2030 target (80%)' }])}
      </div>
    </div>

    <div class="callout">
      <strong>Follow a material to its fate.</strong> The <button class="btn-link" style="display:inline;margin:0;font-weight:600" data-goto="flow">Where&nbsp;it&nbsp;goes</button>
      view draws a flow from every material to whether it's recycled, burned for energy, reused or landfilled.
    </div>
  `;

  // top-materials ranked bars (clickable)
  const barWrap = root.querySelector('[data-role="mat-bars"]') as HTMLElement;
  const top = mats.slice(0, 8);
  barWrap.innerHTML = rankedBarsSvg(
    top.map((m) => ({
      label: catInfo(m.key).short,
      value: m.total,
      colour: catInfo(m.key).colour,
      tip: `${catInfo(m.key).short}: ${formatTonnes(m.total)}\n${formatPercent(m.rate, 0)} recovered`,
      data: { 'open-material': m.key },
    })),
    { width: 520, unit: 't', valueFmt: (v) => formatTonnes(v), clickAttr: 'open-material' },
  );
  barWrap.querySelectorAll('[data-open-material]').forEach((el) =>
    el.addEventListener('click', () => ctx.openMaterial(el.getAttribute('data-open-material') as string)),
  );

  root.querySelectorAll('[data-goto]').forEach((el) => el.addEventListener('click', () => ctx.goTo(el.getAttribute('data-goto') as never)));
}
